import React, { useState, useEffect, useCallback } from 'react';
import { leaveService } from '../services/leaveService';
import './LeaveRequestForm.css';

const LeaveRequestForm = ({ employeeId, employeeName, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    leaveType: 'ANNUAL',
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [balance, setBalance] = useState(null);
  const [calculatedDays, setCalculatedDays] = useState(0);

  const loadBalance = useCallback(async () => {
    try {
      const response = await leaveService.getBalance(employeeId);
      setBalance(response.data);
    } catch (err) {
      console.error('Error loading balance:', err);
    }
  }, [employeeId]);

  const calculateDays = useCallback(() => {
    if (!formData.startDate || !formData.endDate) {
      setCalculatedDays(0);
      return;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (end < start) {
      setCalculatedDays(0);
      return;
    }

    let days = 0;
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclure samedi et dimanche
        days++;
      }
      current.setDate(current.getDate() + 1);
    }
    setCalculatedDays(days);
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    if (employeeId) {
      loadBalance();
    }
  }, [employeeId, loadBalance]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      calculateDays();
    }
  }, [formData.startDate, formData.endDate, calculateDays]);

  const validate = () => {
    const newErrors = {};

    if (!formData.startDate) {
      newErrors.startDate = 'La date de début est obligatoire';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'La date de fin est obligatoire';
    } else if (formData.startDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      newErrors.endDate = 'La date de fin doit être postérieure à la date de début';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Vérifier le solde projeté pour les congés annuels
    if (formData.leaveType === 'ANNUAL' && balance && calculatedDays > 0) {
      const previousYearBalance = balance.previousYearBalance || 0;
      const remainingDays = balance.remainingDays != null ? balance.remainingDays : 0;
      const availableWithPrevious = previousYearBalance + remainingDays;
      const projectedRemaining = availableWithPrevious - calculatedDays;

      if (projectedRemaining < 0) {
        const confirmationMessage = `Cette demande va rendre le solde négatif (${projectedRemaining} jours).\n` +
          'Voulez-vous continuer malgré tout ?';
        const confirmed = window.confirm(confirmationMessage);
        if (!confirmed) {
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      const leaveData = {
        employeeId: employeeId,
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason,
      };

      await leaveService.create(leaveData);
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de la création de la demande';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const leaveTypes = [
    { value: 'ANNUAL', label: 'Congé annuel' },
    { value: 'SICK', label: 'Congé maladie' },
    { value: 'MATERNITY', label: 'Congé maternité' },
    { value: 'PATERNITY', label: 'Congé paternité' },
    { value: 'UNPAID', label: 'Congé sans solde' },
    { value: 'PERSONAL', label: 'Congé spéciaux' },
    { value: 'OTHER', label: 'Autre' },
  ];

  return (
    <div className="leave-request-form">
      <h3>Demande de congé - {employeeName}</h3>

      {balance && formData.leaveType === 'ANNUAL' && (() => {
        const previousYearBalance = balance.previousYearBalance || 0;
        const currentYearTheoretical = balance.calculatedDays != null ? balance.calculatedDays : balance.totalDays;
        const acquiredDays = balance.acquiredDays != null ? balance.acquiredDays : 0;
        const totalBalance = previousYearBalance + currentYearTheoretical;
        const acquiredPlusPrevious = previousYearBalance + acquiredDays;
        const currentActualBalance = acquiredPlusPrevious - (balance.usedDays || 0);
        const theoreticalRemaining = totalBalance - (balance.usedDays || 0);
        return (
          <div className="balance-info">
            <div className="balance-item">
              <span>Solde de l'année précédente :</span>
              <strong>{previousYearBalance} jours</strong>
            </div>
            <div className="balance-item">
              <span>Jours acquis depuis début d'année :</span>
              <strong>{acquiredDays} jours</strong>
            </div>
            <div className="balance-item">
              <span>Solde (année précédente + acquis) :</span>
              <strong>{acquiredPlusPrevious} jours</strong>
            </div>
            <div className="balance-item">
              <span>Solde de l'année actuelle théorique :</span>
              <strong>{currentYearTheoretical} jours</strong>
            </div>
            <div className="balance-item">
              <span>Solde actuelle :</span>
              <strong>{currentActualBalance} jours</strong>
            </div>
            <div className="balance-item">
              <span>Solde théorique restant :</span>
              <strong>{theoreticalRemaining} jours</strong>
            </div>
            <div className="balance-item highlight">
              <span>Total (année précédente + année actuelle) :</span>
              <strong>{totalBalance} jours</strong>
            </div>
            <div className="balance-item">
              <span>Utilisés:</span>
              <strong>{balance.usedDays} jours</strong>
            </div>
            <div className="balance-item">
              <span>En attente:</span>
              <strong>{balance.pendingDays} jours</strong>
            </div>
          </div>
        );
      })()}

      {errors.submit && (
        <div className="error-banner">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="leaveType">Type de congé *</label>
          <select
            id="leaveType"
            name="leaveType"
            value={formData.leaveType}
            onChange={handleChange}
            className={errors.leaveType ? 'error' : ''}
          >
            {leaveTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors.leaveType && <span className="error-message">{errors.leaveType}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">Date de début *</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className={errors.startDate ? 'error' : ''}
            />
            {errors.startDate && <span className="error-message">{errors.startDate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="endDate">Date de fin *</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              className={errors.endDate ? 'error' : ''}
            />
            {errors.endDate && <span className="error-message">{errors.endDate}</span>}
          </div>
        </div>

        {calculatedDays > 0 && (
          <div className="calculated-days">
            <strong>Nombre de jours ouvrés: {calculatedDays}</strong>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="reason">Raison (optionnel)</label>
          <textarea
            id="reason"
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            rows="3"
            placeholder="Raison de la demande de congé..."
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Envoi...' : 'Soumettre la demande'}
          </button>
          {onClose && (
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Annuler
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default LeaveRequestForm;

